import { useState } from 'react'
import { User, Filter, X, CheckSquare } from 'lucide-react'

const STATUS_LABELS = {
  pending:     'Pendiente',
  in_progress: 'En Progreso',
  review:      'En Revisión',
  completed:   'Completado',
  blocked:     'Bloqueado',
}

const STATUS_DOT = {
  pending:     'bg-gray-300',
  in_progress: 'bg-blue-500',
  review:      'bg-purple-500',
  completed:   'bg-green-500',
  blocked:     'bg-red-500',
}

const PRIORITY_COLORS = {
  high:   'text-red-600 bg-red-50 border border-red-100',
  medium: 'text-yellow-700 bg-yellow-50 border border-yellow-100',
  low:    'text-green-700 bg-green-50 border border-green-100',
}

const PRIORITY_LABELS = { high: 'Alta', medium: 'Media', low: 'Baja' }

/**
 * TaskDashboard — reusable task list with filters
 *
 * Props:
 *   tasks: Array<{
 *     id, name, status, priority, description,
 *     assignee_id, assignee: { id, full_name } | null,
 *     phaseName: string, sectionName: string,
 *     sectionAssignee: { id, full_name } | null,  // responsable de la sección
 *   }>
 */
export default function TaskDashboard({ tasks = [] }) {
  const [filterAssignee, setFilterAssignee] = useState('')
  const [filterStatus,   setFilterStatus]   = useState('')

  // Unique assignees — includes both task-level and section-level responsibles
  const assigneeMap = new Map()
  tasks.forEach(t => {
    if (t.assignee)        assigneeMap.set(t.assignee.id,        t.assignee)
    if (t.sectionAssignee) assigneeMap.set(t.sectionAssignee.id, t.sectionAssignee)
  })
  const assignees = [...assigneeMap.values()].sort((a, b) => a.full_name.localeCompare(b.full_name))

  const filtered = tasks.filter(t => {
    if (filterAssignee) {
      const matchesTask    = t.assignee?.id        === filterAssignee
      const matchesSection = t.sectionAssignee?.id === filterAssignee
      if (!matchesTask && !matchesSection) return false
    }
    if (filterStatus && t.status !== filterStatus) return false
    return true
  })

  const hasFilters = filterAssignee || filterStatus

  // Group by phaseName for nicer display
  const grouped = filtered.reduce((acc, task) => {
    const key = task.phaseName || 'Sin fase'
    if (!acc[key]) acc[key] = []
    acc[key].push(task)
    return acc
  }, {})

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 items-center bg-gray-50 rounded-xl p-3 border border-gray-200">
        <Filter size={14} className="text-gray-400 flex-shrink-0" />

        <select
          className="input py-1.5 text-sm flex-1 min-w-[160px]"
          value={filterAssignee}
          onChange={e => setFilterAssignee(e.target.value)}
        >
          <option value="">Todos los responsables</option>
          <option value="__unassigned__">Sin responsable</option>
          {assignees.map(a => (
            <option key={a.id} value={a.id}>{a.full_name}</option>
          ))}
        </select>

        <select
          className="input py-1.5 text-sm flex-1 min-w-[140px]"
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
        >
          <option value="">Todos los estados</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>

        {hasFilters && (
          <button
            onClick={() => { setFilterAssignee(''); setFilterStatus('') }}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-500 transition-colors"
          >
            <X size={12} /> Limpiar
          </button>
        )}

        <span className="ml-auto text-xs text-gray-400 flex-shrink-0">
          {filtered.length} tarea{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <CheckSquare size={36} className="mx-auto mb-3 opacity-20" />
          <p className="font-medium">No hay tareas</p>
          {hasFilters && <p className="text-sm mt-1">Prueba cambiando los filtros</p>}
        </div>
      )}

      {/* Grouped task list */}
      {Object.entries(grouped).map(([phaseName, phaseTasks]) => (
        <div key={phaseName} className="space-y-2">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-1">
            {phaseName}
          </h3>
          {phaseTasks.map(task => (
            <div key={task.id} className="bg-white rounded-xl border border-gray-200 p-3.5 hover:border-brand-300 transition-colors">
              <div className="flex items-start gap-3">
                {/* Status dot */}
                <div className={`mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0 ${STATUS_DOT[task.status] ?? 'bg-gray-200'}`} />

                <div className="flex-1 min-w-0">
                  {/* Task name + priority */}
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <p className={`text-sm font-medium leading-snug ${
                      task.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-800'
                    }`}>
                      {task.name}
                    </p>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {task.priority && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_COLORS[task.priority] ?? ''}`}>
                          {PRIORITY_LABELS[task.priority] ?? task.priority}
                        </span>
                      )}
                      <span className="text-xs text-gray-400">{STATUS_LABELS[task.status] ?? task.status}</span>
                    </div>
                  </div>

                  {/* Meta row */}
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    <span className="text-xs text-gray-400 truncate">
                      {task.sectionName}
                    </span>

                    {/* Task assignee */}
                    {task.assignee ? (
                      <div className="flex items-center gap-1 text-xs text-brand-600 font-medium">
                        <User size={11} />
                        <span>{task.assignee.full_name}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-300 italic">Sin responsable de tarea</span>
                    )}

                    {/* Section assignee (responsable de etapa) */}
                    {task.sectionAssignee && task.sectionAssignee.id !== task.assignee_id && (
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <span className="bg-gray-100 rounded px-1.5 py-0.5">
                          Etapa: {task.sectionAssignee.full_name}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  {task.description && (
                    <p className="text-xs text-gray-400 mt-1 line-clamp-1">{task.description}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}
